import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { ChevronRight, AlertTriangle, CheckCircle, Info, ArrowRight, ChevronDown } from 'lucide-react';

const MaterialBreakdownCanva = () => {
  const { setCurrentScreen } = useApp();
  const [expandedFiber, setExpandedFiber] = useState(null);
  const [product, setProduct] = useState(null);
  const [imageZoomed, setImageZoomed] = useState(false);

  useEffect(() => {
    const savedResult = sessionStorage.getItem('currentScanResult');
    if (savedResult) {
      try {
        const data = JSON.parse(savedResult);
        setProduct(data);
      } catch (e) {
        console.error('Error loading scan result:', e);
      }
    }
  }, []);

  if (!product) {
    return (
      <div className="min-h-screen bg-white pb-24 flex items-center justify-center">
        <p className="text-black/55">Loading sustainability analysis...</p>
      </div>
    );
  }

  // Parse composition from product (handles multiple formats)
  const parseComposition = () => {
    if (!product.composition) return [];
    
    let compositionString = product.composition;
    let parts = [];
    
    // Strategy 1: Split by comma or semicolon
    if (compositionString.includes(',') || compositionString.includes(';')) {
      parts = compositionString.split(/,|;/).map(p => p.trim());
    } 
    // Strategy 2: Multiple percentage signs (e.g., "99% Cotton 1% Spandex")
    else if ((compositionString.match(/%/g) || []).length > 1) {
      const percentMatches = compositionString.match(/(\d+%?\s*[A-Za-z\s]+)/g);
      if (percentMatches) {
        parts = percentMatches.map(p => p.trim());
      }
    }
    // Strategy 3: Single fiber
    else {
      parts = [compositionString];
    }
    
    return parts.map(part => {
      const p = part.trim();
      let percentage = null;
      let name = null;

      const fwd = p.match(/^(\d+)\s*%?\s+(.+)$/);       // "100% Cotton" or "100 Cotton"
      const rev = p.match(/^(.+?)\s+(\d+)\s*%?$/);       // "Cotton 100%" or "Cotton 100"
      const colon = p.match(/^(.+?)\s*:\s*(\d+)\s*%?$/); // "Cotton: 100%"

      if (fwd) {
        percentage = parseInt(fwd[1]);
        name = fwd[2].trim();
      } else if (colon) {
        name = colon[1].trim();
        percentage = parseInt(colon[2]);
      } else if (rev) {
        name = rev[1].trim();
        percentage = parseInt(rev[2]);
      }

      if (!name || !percentage || name === '%') return null;

      const isNatural = /cotton|wool|silk|linen|cashmere|hemp|jute/i.test(name);
      const isSynthetic = /polyester|nylon|acrylic|elastane|polyamide|spandex/i.test(name);

      return {
        name,
        percentage,
        type: isNatural ? 'natural' : isSynthetic ? 'synthetic' : 'unknown',
        score: isNatural ? 75 : isSynthetic ? 40 : 60,
        risks: isNatural
          ? ['High water usage (if not organic)', 'Pesticide concerns for conventional']
          : ['Non-biodegradable', 'Microplastic shedding', 'Fossil fuel based'],
        lifespan: isNatural ? '3-5 years' : '5-10 years'
      };
    }).filter(Boolean);
  };

  const fibers = parseComposition();
  
  // Calculate sustainability metrics from REAL eco-tier data (not just natural vs synthetic)
  const calculateSustainabilityAssessment = () => {
    if (fibers.length === 0) {
      return { assessment: 'Unknown', riskLevel: 'medium', avgScore: 60 };
    }
    
    // Calculate weighted average sustainability score based on fiber percentages
    let totalScore = 0;
    let totalPercentage = 0;
    
    fibers.forEach(fiber => {
      // Get eco-tier based score for each fiber
      const ecoScore = getEcoTierScore(fiber.name);
      totalScore += ecoScore * fiber.percentage;
      totalPercentage += fiber.percentage;
    });
    
    const avgScore = totalPercentage > 0 ? totalScore / totalPercentage : 60;
    
    // Determine assessment based on average eco score
    let assessment, riskLevel;
    if (avgScore >= 75) {
      assessment = 'Excellent Sustainability';
      riskLevel = 'low';
    } else if (avgScore >= 60) {
      assessment = 'Good Sustainability';
      riskLevel = 'low';
    } else if (avgScore >= 45) {
      assessment = 'Moderate Impact';
      riskLevel = 'medium';
    } else {
      assessment = 'High Environmental Impact';
      riskLevel = 'high';
    }
    
    return { assessment, riskLevel, avgScore: Math.round(avgScore) };
  };
  
  // Helper function to get eco-tier score for a fiber
  const getEcoTierScore = (fiberName) => {
    const name = fiberName.toLowerCase();
    // Based on fabric database eco-tiers (converted to 0-100)
    if (/organic cotton|recycled/.test(name)) return 90; // Eco-tier A
    if (/linen|hemp|tencel|modal/.test(name)) return 75; // Eco-tier B
    if (/cotton|wool|silk/.test(name)) return 60; // Eco-tier C (Moderate!)
    if (/polyester|nylon/.test(name)) return 40; // Eco-tier D
    if (/acrylic|cashmere/.test(name)) return 20; // Eco-tier E (Cashmere is disaster!)
    return 60; // Default
  };
  
  const { assessment, riskLevel, avgScore } = calculateSustainabilityAssessment();
  
  // Calculate natural vs synthetic percentage
  const naturalPercent = fibers.filter(f => f.type === 'natural').reduce((sum, f) => sum + f.percentage, 0);
  const syntheticPercent = fibers.filter(f => f.type === 'synthetic').reduce((sum, f) => sum + f.percentage, 0);
  
  // Get color for sustainability score
  const getScoreColor = (score) => {
    if (score >= 70) return '#16A34A';
    if (score >= 40) return '#EAB308';
    return '#DC2626';
  };
  
  const sustainabilityColor = getScoreColor(avgScore);

  // Sort fibers by risk (synthetics first, then by score)
  const sortedFibers = [...fibers].sort((a, b) => {
    if (a.type === 'synthetic' && b.type === 'natural') return -1;
    if (a.type === 'natural' && b.type === 'synthetic') return 1;
    return a.score - b.score;
  });

  return (
    <div className="min-h-screen bg-white pb-24">
      <div className="px-6 pt-6 pb-4 flex items-center gap-4 border-b border-black/10">
        <button
          onClick={() => setCurrentScreen('results-canva')}
          className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center hover:bg-black/10 transition-all flex-shrink-0"
        >
          <ChevronRight size={18} className="text-black rotate-180" />
        </button>
        <button onClick={() => setActiveTab("home")} className="hover:opacity-80 transition-opacity"><img src="/logos/logo-black.jpg" alt="ATELIA" className="h-16 w-auto object-contain" /></button>
      </div>

      <div className="px-6 pt-6 pb-4 flex gap-4 items-start">
        {(product.imageUrl || product.thumbnailUrl) && (
          <button
            onClick={() => setImageZoomed(true)}
            className="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-black/10 hover:opacity-90 transition-opacity"
          >
            <img
              src={product.imageUrl || product.thumbnailUrl}
              alt={product.name}
              className="w-full h-full object-cover object-center"
            />
          </button>
        )}
        <div>
          <p className="text-xs uppercase tracking-wide text-black/55 mb-1 font-bold">SUSTAINABILITY ANALYSIS</p>
          <h1 className="text-2xl font-bold text-black uppercase tracking-wide">Material Breakdown</h1>
          <p className="text-sm text-black/55 mt-1">{product.brand} • {product.name}</p>
        </div>
      </div>

      {imageZoomed && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setImageZoomed(false)}
        >
          <img
            src={product.imageUrl || product.thumbnailUrl}
            alt={product.name}
            className="max-w-full max-h-full rounded-xl object-contain"
          />
        </div>
      )}

      <div className="px-6 mb-6">
        <div className={'rounded-2xl p-5 border-2 ' + (
          riskLevel === 'high' ? 'bg-red-50 border-red-200' : 
          riskLevel === 'medium' ? 'bg-yellow-50 border-yellow-200' : 
          'bg-green-50 border-green-200'
        )}>
          <div className="flex items-center gap-3">
            {riskLevel === 'high' ? (
              <AlertTriangle size={32} className="text-red-600" />
            ) : riskLevel === 'medium' ? (
              <Info size={32} className="text-yellow-600" />
            ) : (
              <CheckCircle size={32} className="text-green-600" />
            )}
            <div>
              <p className="text-lg font-bold text-black uppercase">{assessment}</p>
              <p className="text-sm text-black/72">
                {riskLevel === 'high' ? 'High environmental impact materials detected' : 
                 riskLevel === 'medium' ? 'Mixed environmental impact' :
                 'Low environmental impact - good choice!'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 mb-6">
        <p className="text-xs uppercase tracking-wide text-black/55 mb-3 font-bold">Materials (Sorted by Risk)</p>
        <div className="space-y-2">
          {sortedFibers.map((fiber, i) => (
            <div key={i} className="bg-white border border-black/12 rounded-xl overflow-hidden">
              <button
                onClick={() => setExpandedFiber(expandedFiber === i ? null : i)}
                className="w-full p-4 flex items-center justify-between hover:bg-black/5"
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{backgroundColor: fiber.type === 'synthetic' ? '#DC2626' : '#16A34A'}} 
                  />
                  <div className="text-left">
                    <p className="text-sm font-bold text-black uppercase">{fiber.name}</p>
                    <p className="text-xs text-black/55">{fiber.percentage}% • Score: {fiber.score}/100</p>
                  </div>
                </div>
                <ChevronDown 
                  size={16} 
                  className={'text-black/30 transition-transform ' + (expandedFiber === i ? 'rotate-180' : '')} 
                />
              </button>

              {expandedFiber === i && (
                <div className="border-t border-black/12 p-4 bg-black/5">
                  <p className="text-xs uppercase font-bold text-black/55 mb-2">Environmental Risks</p>
                  <ul className="space-y-1 mb-3">
                    {fiber.risks.map((risk, j) => (
                      <li key={j} className="text-sm text-black/72 flex items-start gap-2">
                        <span>•</span>
                        <span>{risk}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex justify-between text-xs">
                    <span className="text-black/55">Expected Lifespan:</span>
                    <span className="font-bold text-black">{fiber.lifespan}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="px-6">
        <button
          onClick={() => setCurrentScreen('alternatives-canva')}
          className="w-full bg-black text-white py-4 rounded-xl font-bold uppercase flex items-center justify-center gap-2"
        >
          View Alternatives
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default MaterialBreakdownCanva;
